import { Component, ChangeDetectionStrategy, input, output, signal, computed, effect, inject, ElementRef, DestroyRef, afterNextRender } from '@angular/core';
import { IconComponent, IconSize } from '@shared/components/ui/icon';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'danger' | 'success' | 'info' | 'warning' | 'help' | 'outline-danger' | 'outline-success';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'small';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonComponent {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  // Legacy callers bind ARIA to the custom host. Mirror only accessibility
  // attributes; explicit component inputs remain authoritative.
  private readonly hostAria = signal<Record<string, string | null>>({});
  protected readonly nativeAria = computed(() => {
    const host = this.hostAria();
    return {
      label: this.ariaLabel() ?? host['aria-label'],
      labelledBy: this.ariaLabelledBy() ?? host['aria-labelledby'],
      describedBy: this.ariaDescribedBy() ?? host['aria-describedby'],
      expanded: this.ariaExpanded() ?? host['aria-expanded'],
      pressed: this.ariaPressed() ?? host['aria-pressed'],
      controls: this.ariaControls() ?? host['aria-controls'],
      hasPopup: this.ariaHasPopup() ?? host['aria-haspopup'],
    };
  });
  // Aliased input feeds writable signal so setters (outlined/severity) can mutate variant
  readonly variantInput = input<ButtonVariant>('primary', { alias: 'variant' });
  readonly variant = signal<ButtonVariant>('primary');

  readonly size = input<ButtonSize>('md');
  readonly disabled = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly fullWidth = input<boolean>(false);
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly ariaLabelledBy = input<string | undefined>(undefined);
  readonly ariaHasPopup = input<string | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly ariaDescribedBy = input<string | undefined>(undefined);
  readonly ariaExpanded = input<boolean | undefined>(undefined);
  readonly ariaPressed = input<boolean | undefined>(undefined);
  readonly ariaControls = input<string | undefined>(undefined);
  readonly tabIndex = input<number>(0);
  readonly rounded = input<boolean>(false); // Razor UI button compatibility
  readonly text = input<boolean>(false); // Text-only button (no background)
  readonly link = input<boolean>(false); // Link-style button (for navigation)
  readonly tooltipLabel = input<string | undefined>(undefined); // Tooltip text for hover

  // Setter input: outlined → forces variant to 'outline' when true
  readonly outlinedInput = input<boolean>(false, { alias: 'outlined' });

  // Razor UI button compatibility
  readonly label = input<string | undefined>(undefined); // For content when no ng-content provided
  // GAP-FE-PRIME-P2-SHARED · icon now accepts a <ui-icon> name
  // (e.g. 'check'). Legacy 'pi pi-X' strings still work because
  // resolveIconName tokenizes and strips the prefix.
  readonly icon = input<string | undefined>(undefined);
  readonly iconPos = input<'left' | 'right'>('left');
  readonly styleClass = input<string | undefined>(undefined); // Additional CSS classes

  // Setter input: severity → maps legacy severity strings to variants
  readonly severityInput = input<string | undefined>(undefined, { alias: 'severity' });

  readonly clicked = output<Event>();
  readonly onClick = output<Event>(); // backward compatibility alias

  readonly buttonClasses = computed<string>(() => {
    const borderRadius = this.rounded() ? 'rounded-full' : 'rounded-md';
    const baseClasses = `inline-flex items-center justify-center ${borderRadius} font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bb-accent-blue/20 disabled:pointer-events-none disabled:opacity-50`;

    const variantClasses: Record<ButtonVariant, string> = {
      primary: 'bg-bb-accent-blue text-bb-bg-primary hover:brightness-95 dark:hover:brightness-110',
      secondary: 'bg-bb-bg-secondary text-bb-text-primary hover:bg-bb-bg-tertiary',
      outline: 'border border-bb-border bg-transparent hover:bg-bb-bg-secondary',
      ghost: 'hover:bg-bb-bg-secondary',
      destructive: 'bg-bb-accent-red text-bb-bg-primary hover:brightness-95 dark:hover:brightness-110',
      danger: 'bg-bb-accent-red text-bb-bg-primary hover:brightness-95 dark:hover:brightness-110',
      success: 'bg-bb-accent-green text-white hover:bg-bb-accent-green/90',
      info: 'bg-bb-accent-blue text-bb-bg-primary hover:brightness-95 dark:hover:brightness-110',
      warning: 'bg-bb-accent-amber text-white hover:bg-bb-accent-amber/90',
      help: 'bg-bb-accent-purple text-white hover:bg-bb-accent-purple/90',
      'outline-danger': 'border border-bb-accent-red text-bb-accent-red bg-transparent hover:bg-bb-accent-red/10',
      'outline-success': 'border border-bb-accent-green text-bb-accent-green bg-transparent hover:bg-bb-accent-green/10'
    };

    const textOnlyClasses = this.text() ? 'bg-transparent hover:bg-bb-bg-secondary' : '';

    const sizeClasses: Record<string, string> = {
      sm: 'min-h-11 min-w-11 px-3 text-xs',      // 44px minimum - WCAG touch target
      small: 'min-h-11 min-w-11 px-3 text-xs',   // 44px minimum - WCAG touch target
      md: 'h-11 px-4 text-sm',                   // 44px - WCAG compliant
      lg: 'h-12 px-8 text-base'                  // 48px - already compliant
    };

    const widthClass = this.fullWidth() ? 'w-full' : '';

    return `${baseClasses} ${textOnlyClasses || variantClasses[this.variant()]} ${sizeClasses[this.size()]} ${widthClass}`;
  });

  /**
   * GAP-FE-PRIME-P2-SHARED r2 (Codex) · Map the button's `size`
   * input to the corresponding `<ui-icon>` size so icons scale
   * with the surrounding type. Before this mapping, every button
   * icon was hard-coded to `sm` (h-4/w-4 = 16px), which over-sized
   * dense sm/small buttons and under-sized lg buttons.
   *   button sm | small → icon xs  (12px)
   *   button md          → icon sm (16px)
   *   button lg          → icon md (20px)
   */
  protected readonly iconSize = computed<IconSize>(() => {
    const size = this.size();
    if (size === 'sm' || size === 'small') { return 'xs'; }
    if (size === 'lg') { return 'md'; }
    return 'sm';
  });

  constructor() {
    const attributes = ['aria-label', 'aria-labelledby', 'aria-describedby',
      'aria-expanded', 'aria-pressed', 'aria-controls', 'aria-haspopup'];
    const readHostAria = () => this.hostAria.set(Object.fromEntries(
      attributes.map(name => [name, this.element.nativeElement.getAttribute(name)]),
    ));
    // Static host attributes already exist here. Observe later Angular
    // [attr.aria-*] updates as well, including null/removal on OnPush callers.
    readHostAria();
    afterNextRender(() => {
      readHostAria();
      const Observer = this.element.nativeElement.ownerDocument.defaultView?.MutationObserver;
      if (!Observer) { return; }
      const observer = new Observer(readHostAria);
      observer.observe(this.element.nativeElement, { attributes: true, attributeFilter: attributes });
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
    // Mirror variant input → writable signal (default sync)
    effect(() => {
      this.variant.set(this.variantInput());
    });

    // outlined setter behavior: when true, force variant to 'outline'
    effect(() => {
      if (this.outlinedInput()) {
        this.variant.set('outline');
      }
    });

    // severity setter behavior: map legacy severity strings to variants
    effect(() => {
      const value = this.severityInput();
      if (value === undefined) {
        return;
      }
      const severityMap: Record<string, ButtonVariant> = {
        'primary': 'primary',
        'secondary': 'secondary',
        'success': 'success',
        'info': 'info',
        'warning': 'warning',
        'help': 'help',
        'danger': 'danger'
      };
      this.variant.set(severityMap[value] || 'primary');
    });
  }

  handleClick(event: Event): void {
    if (!this.disabled() && !this.loading()) {
      this.clicked.emit(event);
      this.onClick.emit(event); // Emit to both for compatibility
    }
  }
}
